import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PurlivoAccountSwitch } from '@shared/common/session/purlivo-account-switch';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { AppUrlService } from '@shared/common/nav/app-url.service';
import {
    AccountServiceProxy,
    ImpersonateUserInput,
    ImpersonateTenantInput,
    ImpersonateOutput,
    DelegatedImpersonateInput,
} from '@shared/service-proxies/service-proxies';

@Injectable()
export class ImpersonationService {
    private _accountService = inject(AccountServiceProxy);
    private _appUrlService = inject(AppUrlService);
    private _authService = inject(AppAuthService);

    impersonateTenant(userId: number, tenantId?: number): void {
        const input = new ImpersonateTenantInput();
        input.userId = userId;
        input.tenantId = tenantId;

        this._accountService.impersonateTenant(input).subscribe(result => this.enter(result, input.tenantId));
    }

    impersonateUser(userId: number, tenantId: number): void {
        const input = new ImpersonateUserInput();
        input.userId = userId;
        input.tenantId = tenantId;

        this._accountService.impersonateUser(input).subscribe(result => this.enter(result, input.tenantId));
    }

    delegatedImpersonate(userDelegationId: number, tenantId?: number): void {
        const input = new DelegatedImpersonateInput();
        input.userDelegationId = userDelegationId;

        this._accountService.delegatedImpersonate(input).subscribe((result: ImpersonateOutput) => {
            let targetUrl = this.getAppRootUrl(result) + '?impersonationToken=' + result.impersonationToken;
            targetUrl = targetUrl + '&userDelegationId=' + userDelegationId;
            if (tenantId) {
                targetUrl = targetUrl + '&tenantId=' + tenantId;
            }

            this._authService.logout(true, targetUrl);
        });
    }

    backToImpersonator(): void {
        void PurlivoAccountSwitch.run(async () => {
            const result = await firstValueFrom(this._accountService.backToImpersonator());
            let targetUrl: string;
            if (result.returnsToHost) {
                if (result.portalUrl !== 'https://host-dev.purlivo.com/app/admin/tenants') throw new Error('Invalid support return');
                targetUrl = result.portalUrl;
            } else {
                targetUrl = this.getAppRootUrl(result) + '?impersonationToken=' + encodeURIComponent(result.impersonationToken!);
                if (abp.session.impersonatorTenantId) targetUrl += '&tenantId=' + abp.session.impersonatorTenantId;
            }
            await new Promise<void>((resolve, reject) => this._authService.logout(true, targetUrl, resolve, () => reject(new Error('Native logout failed'))));
        }).then(status => {
            if (status !== 'completed') abp.message.warn(abp.localization.localize('PurlivoReopenWorkspace', 'UVSAN'));
        }).catch(() => abp.message.error(abp.localization.localize('ImpersonationTokenErrorMessage', 'UVSAN')));
    }

    private enter(result: ImpersonateOutput, tenantId?: number): void {
        const target = this.getAppRootUrl(result) + '?impersonationToken=' + encodeURIComponent(result.impersonationToken!) +
            (tenantId ? '&tenantId=' + tenantId : '');
        if (result.portalUrl) {
            if (result.portalUrl !== 'https://app-dev.purlivo.com/index.html') throw new Error('Invalid support destination');
            // Native host authentication stays on its own origin; only a one-use capability crosses surfaces.
            location.href = target;
        } else this._authService.logout(true, target);
    }

    getAppRootUrl(result: ImpersonateOutput): string {
        return result.portalUrl ?? (this._appUrlService.getAppRootUrlOfTenant(result.tenancyName) + 'index.html');
    }
}
